import { Button, Text } from '@tunein/react-ui';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useContext, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { changePassword } from 'src/common/actions/auth';
import {
  CHANGE_PASSWORD,
  CHANGE_PASSWORD_FAILED,
  CHANGE_PASSWORD_SUBMIT_TEXT,
  CONFIRM_NEW_PASSWORD,
  CONFIRM_NEW_PASSWORD_IS_REQUIRED,
  NEW_PASSWORD,
  NEW_PASSWORD_IS_REQUIRED,
} from 'src/common/constants/localizations/settings';
import { PASSWORDS_DONT_MATCH } from 'src/common/constants/localizations/shared';
import getRequiredLabel from 'src/common/utils/form/getRequiredLabel';
import useActions from '../../../hooks/useActions';
import { LocationAndLocalizationContext } from '../../../providers/LocationAndLocalizationProvider';
import {
  composeValidators,
  isMatch,
  isRequired,
  isValidPassword,
} from '../../../utils/form/validation';
import authCss from '../../auth/auth.module.scss';
import ShowPasswordButton from '../../shared/button/ShowPasswordButton';
import FormInput from '../../shared/form/FormInput';
import TopLevelErrorContainer from '../../shared/form/TopLevelErrorContainer';
import commonSettingsCss from '../account-settings.module.scss';
import css from './general-settings.module.scss';

const fieldOrder = ['newPassword', 'confirmNewPassword'];

export default function ChangePasswordForm({ handleSuccessfulPasswordChange }) {
  const { getLocalizedText } = useContext(LocationAndLocalizationContext);
  const actions = useActions({ changePassword });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const onSubmit = async (data) => {
    try {
      await actions.changePassword(data?.newPassword);
      handleSuccessfulPasswordChange();
    } catch (error) {
      return { base: CHANGE_PASSWORD_FAILED };
    }
  };

  const handleShowPasswordClick = (stateSetter, passwordState, ev) => {
    ev.preventDefault();
    stateSetter(!passwordState);
  };

  return (
    <div className={authCss.withFormSections}>
      <Form onSubmit={onSubmit}>
        {({ handleSubmit, errors, pristine, touched, invalid, submitting, values }) => (
          <form name="changePassword" data-testid="changePasswordForm" onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-xs-12">
                <Text
                  as="h1"
                  className={commonSettingsCss.contentTitle}
                  data-testid="changePasswordTitle"
                >
                  {getLocalizedText(CHANGE_PASSWORD)}
                </Text>
              </div>
            </div>
            {errors && (!pristine || touched) && (
              <div className={commonSettingsCss.errorContainer}>
                <TopLevelErrorContainer
                  className={clsx(authCss.largeSection, authCss.noSpacing)}
                  baseErrorClassName={authCss.baseErrorClassName}
                  customErrorListClass={authCss.customErrorListClass}
                  errorTextClassName={clsx(
                    authCss.customErrorTextClass,
                    commonSettingsCss.errorList,
                  )}
                  errorMessages={errors}
                  fieldOrder={fieldOrder}
                  touchedFields={touched}
                  dedupeFields={{ confirmNewPassword: 'newPassword' }}
                />
              </div>
            )}

            <div className={commonSettingsCss.section}>
              <div className="row">
                <div className="col-xs-12">
                  <div data-testid="newPasswordInput" className={commonSettingsCss.inputWrapper}>
                    <Field
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      component={FormInput}
                      hideInlineError
                      dataTestId="newPassword"
                      name="newPassword"
                      placeholder={getRequiredLabel(getLocalizedText(NEW_PASSWORD))}
                      validate={composeValidators(
                        isRequired(NEW_PASSWORD_IS_REQUIRED),
                        isValidPassword,
                      )}
                      required
                    />
                    {values?.newPassword && (
                      <ShowPasswordButton
                        showPassword={showNewPassword}
                        onClick={(ev) =>
                          handleShowPasswordClick(setShowNewPassword, showNewPassword, ev)
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12">
                  <div
                    data-testid="confirmNewPasswordInput"
                    className={commonSettingsCss.inputWrapper}
                  >
                    <Field
                      id="confirmNewPassword"
                      type={showConfirmNewPassword ? 'text' : 'password'}
                      component={FormInput}
                      hideInlineError
                      dataTestId="confirmNewPassword"
                      name="confirmNewPassword"
                      placeholder={getRequiredLabel(getLocalizedText(CONFIRM_NEW_PASSWORD))}
                      validate={composeValidators(
                        isRequired(CONFIRM_NEW_PASSWORD_IS_REQUIRED),
                        isValidPassword,
                        isMatch('newPassword', PASSWORDS_DONT_MATCH),
                      )}
                      required
                    />
                    {values?.confirmNewPassword && (
                      <ShowPasswordButton
                        showPassword={showConfirmNewPassword}
                        onClick={(ev) =>
                          handleShowPasswordClick(
                            setShowConfirmNewPassword,
                            showConfirmNewPassword,
                            ev,
                          )
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-xs-12">
                <Button
                  data-testid="changePasswordSubmit"
                  id="changePasswordSubmit"
                  type="submit"
                  className={clsx(css.dialogButton, css.dialogPrimaryButton)}
                  disabled={submitting || invalid}
                  radius="full"
                  size="xs"
                  variant="primary"
                >
                  {getLocalizedText(CHANGE_PASSWORD_SUBMIT_TEXT)}
                </Button>
              </div>
            </div>
          </form>
        )}
      </Form>
    </div>
  );
}

ChangePasswordForm.propTypes = {
  handleSuccessfulPasswordChange: PropTypes.func.isRequired,
};
